import Currency from "./global/SiteConstants/Currency";
import Timezone from "./global/SiteConstants/Timezone";
import Language from "./global/SiteConstants/Language";

class GeneralSettings {
    constructor({
                    id = null,
                    organization_id = null,
                    name = '',
                    description = '',
                    logo = null,
                    organization_type_id = '',
                    organization_type_name = '',
                    email = '',
                    phone = '',
                    address = '',
                    website = '',
                    phone_country_id = '',
                    country_id = '',
                    founded_date = '',
                    tax_id = '',
                    vat_id = '',
                    tax_id_verified = false,
                    currency = '',
                    timezone = '',
                    language = '',
                    default_item_endpoint = '',
                    default_item_api_endpoint = '',
                    default_user_parameter = '',
                    notification_preferences = '',
                    send_notifications_via_email = false,
                    send_notifications_via_sms = false,
                    send_notifications_via_push = false,
                    billing_email = '',
                    notification_email = ''
                } = {}) {
        this.id = id;
        this.organization_id = organization_id;
        this.name = name;
        this.description = description;
        this.logo = logo;
        this.organization_type_id = organization_type_id;
        this.organization_type_name = organization_type_name;
        this.email = email;
        this.phone = phone;
        this.address = address;
        this.website = website;
        this.phone_country_id = phone_country_id;
        this.country_id = country_id;
        this.founded_date = founded_date;
        this.tax_id = tax_id;
        this.vat_id = vat_id;
        this.tax_id_verified = tax_id_verified;
        this.currency = currency;
        this.timezone = timezone;
        this.language = language;
        this.default_item_endpoint = default_item_endpoint;
        this.default_item_api_endpoint = default_item_api_endpoint;
        this.default_user_parameter = default_user_parameter;
        this.notification_preferences = notification_preferences;
        this.send_notifications_via_email = send_notifications_via_email;
        this.send_notifications_via_sms = send_notifications_via_sms;
        this.send_notifications_via_push = send_notifications_via_push;
        this.billing_email = billing_email;
        this.notification_email = notification_email;
    }

  /** Full FormData — still useful for file uploads */
    toFormData() {
        const formData = new FormData();
        if (this.id) formData.append('id', this.id);
        if (this.name) formData.append('name', this.name);
        if (this.description) formData.append('description', this.description);
        if (this.logo instanceof File) formData.append('logo', this.logo);
        if (this.organization_type_id) formData.append('organization_type_id', this.organization_type_id);
        if (this.email) formData.append('email', this.email);
        if (this.phone) formData.append('phone', this.phone);
        if (this.address) formData.append('address', this.address);
        if (this.website) formData.append('website', this.website);
        if (this.phone_country_id) formData.append('phone_country_id', this.phone_country_id);
        if (this.country_id) formData.append('country_id', this.country_id);
        if (this.founded_date) formData.append('founded_date', this.founded_date);
        if (this.currency) formData.append('currency', this.currency);
        if (this.timezone) formData.append('timezone', this.timezone);
        if (this.language) formData.append('language', this.language);
        if (this.default_item_endpoint) formData.append('default_item_endpoint', this.default_item_endpoint);
        if (this.default_item_api_endpoint) formData.append('default_item_api_endpoint', this.default_item_api_endpoint);
        if (this.default_user_parameter) formData.append('default_user_parameter', this.default_user_parameter);
        if (this.notification_preferences) formData.append('notification_preferences', this.notification_preferences);
        formData.append('send_notifications_via_email', this.send_notifications_via_email ? '1' : '0');
        formData.append('send_notifications_via_sms', this.send_notifications_via_sms ? '1' : '0');
        formData.append('send_notifications_via_push', this.send_notifications_via_push ? '1' : '0');
        if (this.billing_email) formData.append('billing_email', this.billing_email);
        if (this.notification_email) formData.append('notification_email', this.notification_email);
        return formData;
    }

  /** Only Basic Info fields as FormData */
  toBasicInfoFormData() {
    const formData = new FormData();
    if (this.name) formData.append('name', this.name);
    if (this.description) formData.append('description', this.description);
    if (this.logo instanceof File) formData.append('logo', this.logo);
    if (this.organization_type_id) formData.append('organization_type_id', this.organization_type_id);
    if (this.email) formData.append('email', this.email);
    if (this.phone) formData.append('phone', this.phone);
    if (this.address) formData.append('address', this.address);
    if (this.website) formData.append('website', this.website);
    if (this.phone_country_id) formData.append('phone_country_id', this.phone_country_id);
    if (this.country_id) formData.append('country_id', this.country_id);
    if (this.founded_date) formData.append('founded_date', this.founded_date);
    formData.append('tax_id', this.tax_id || '');
    formData.append('vat_id', this.vat_id || '');
    return formData;
  }

  /** JSON payload for Localization */
  toLocalizationPayload() {
    return {
      currency: typeof this.currency === 'string' ? this.currency : this.currency.key || '',
      timezone: typeof this.timezone === 'string' ? this.timezone : this.timezone.name || '',
      language: typeof this.language === 'string' ? this.language : this.language.iso_code || ''
    };
  }

  /** JSON payload for Integration */
  toIntegrationPayload() {
    return {
      default_item_endpoint: this.default_item_endpoint,
      default_item_api_endpoint: this.default_item_api_endpoint,
      default_user_parameter: this.default_user_parameter
    };
  }

  /** JSON payload for Notifications */
  toNotificationsPayload() {
    return {
      notification_preferences: this.notification_preferences,
      send_notifications_via_email: this.send_notifications_via_email,
      send_notifications_via_sms: this.send_notifications_via_sms,
      send_notifications_via_push: this.send_notifications_via_push,
      notification_email: this.notification_email
    };
  }

    static fromApiResponse(data) {
        return new GeneralSettings({
            id: data.id,
            organization_id: data.organization_id,
            name: data.name || '',
            description: data.description || '',
            logo: data.logo_url || null,
            organization_type_id: data.organization_type_id || '',
            organization_type_name: data.organization_type_name || '',
            email: data.email || '',
            phone: data.phone || '',
            address: data.address || '',
            website: data.website || '',
            phone_country_id: data.phone_country_id || '',
            country_id: data.country_id || '',
            founded_date: data.founded_date || '',
            tax_id: data.tax_id || '',
            vat_id: data.vat_id || '',
            tax_id_verified: !!data.tax_id_verified,
      currency: new Currency(data.currency || {}),
      timezone: new Timezone(data.timezone || {}),
      language: new Language(data.language || {}),
            default_item_endpoint: data.default_item_endpoint || '',
            default_item_api_endpoint: data.default_item_api_endpoint || '',
            default_user_parameter: data.default_user_parameter || '',
      notification_preferences: JSON.stringify(data.notification_preferences || {}),
            send_notifications_via_email: !!data.send_notifications_via_email,
            send_notifications_via_sms: !!data.send_notifications_via_sms,
            send_notifications_via_push: !!data.send_notifications_via_push,
            billing_email: data.billing_email || '',
            notification_email: data.notification_email || ''
        });
    }
}

export default GeneralSettings;
